/**
 * Service-worker registration is intentionally disabled.
 *
 * Earlier versions of WebSurf shipped a SW that cached /api/proxy responses
 * and the React app shell. That caching frequently caused white-screen
 * regressions across redeploys because the SW kept serving stale bundles.
 *
 * We now:
 *   1. Don't register any new SW.
 *   2. Aggressively unregister whatever SW the browser already has for our
 *      origin (left over from old deploys), and wipe every cache it wrote.
 *   3. If we found and removed an old SW, do a one-time hard reload so the
 *      page that's currently visible isn't running stale bundle hashes.
 */
export async function registerServiceWorker(): Promise<undefined> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;

  let didCleanup = false;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      try { await registration.unregister(); didCleanup = true; } catch (_) { /* ignore */ }
    }
  } catch (error) {
    console.warn('Failed to enumerate service worker registrations', error);
  }

  if (typeof caches !== 'undefined') {
    try {
      const keys = await caches.keys();
      if (keys.length) didCleanup = true;
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (error) {
      console.warn('Failed to purge stale caches', error);
    }
  }

  if (didCleanup && !sessionStorage.getItem('websurf:cleaned-sw')) {
    try { sessionStorage.setItem('websurf:cleaned-sw', '1'); } catch (_) {}
    location.reload();
  }

  return undefined;
}
